/**
 * Browser URL routing v2 - single source of truth (ADR-0012).
 * Storage paths keep .md/.html; address bar is extensionless for documents.
 */

import type { OpenTab } from '@/components/features/document-tabs'
import { slugAliasesForWorkspace } from '@/lib/workspace-slug-aliases'
import {
  BASECAMP_SEGMENT_LITERALS,
  stripOptionalBasecampWorkspaceSegment,
} from '@/lib/basecamp-url'
import { BASECAMP_SECTION_PATH_SEGMENTS } from '@/lib/basecamp-tab-routing'
import { isDocumentDeepLinkRestorePending } from '@/lib/document-deep-link-pending'
import { personUserIdFromTabId } from '@/lib/person-view'
import {
  activityQueryFromStorage,
  appendQuery,
  resolveBasecampIndexSection,
  todosQueryFromStorage,
} from '@/lib/index-query-url'

export const STRIP_EXTENSIONS = ['.html', '.md'] as const

export const IMAGE_DOC_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'] as const

function hasStoredDocExtension(path: string): boolean {
  const lower = path.toLowerCase()
  return (
    lower.endsWith('.md') ||
    lower.endsWith('.html') ||
    IMAGE_DOC_EXTENSIONS.some((ext) => lower.endsWith(ext))
  )
}

export const GLOBAL_RESERVED_SEGMENTS = new Set([
  'inbox',
  'search',
  'edit-history',
  'settings',
  'login',
  'signup',
  'forgot-password',
  'reset-password',
  'invites',
  'people',
  'folder',
  'doc',
  'explore',
  'workspace',
  'compare',
  'integrations',
  'mcp-oauth',
  'connect',
  'basecamp',
  'voice-memos',
  'product-board',
  'drive',
  'nextcloud',
  'flow-studio',
])

export const WORKSPACE_EXTENSION_SEGMENTS = new Set([
  'basecamp',
  /** Legacy reserved segment; UX is Inbox + voice filter, not a slug route. See ADR-0012. */
  'voice-memos',
  /** Legacy alias; canonical board is a product-board markdown doc, not this path. See ADR-0012. */
  'product-board',
])

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export type WorkspaceRouteInfo = {
  id: string
  slug: string
  /** Optional display name (explorer breadcrumbs at workspace root). */
  name?: string | null
  /** Storage prefix (e.g. hop-studios/ or ws/{orgIdNoHyphens}/). Used for slug-shaped URL translation. */
  contentRoot?: string | null
}

const WS_OPAQUE_URL_RE = /^\/ws\/([0-9a-f]{32})(\/.*)?$/i
const WS_STORAGE_HEX_PREFIX_RE = /^ws\/([0-9a-f]{32})(\/|$)/i

function normalizeContentRoot(root: string): string {
  return root.replace(/^\/+/, '').replace(/\/+$/, '')
}

function workspaceIdToStorageHex(orgId: string): string {
  return orgId.replace(/-/g, '').toLowerCase()
}

/** Resolve workspace from ws/{orgIdNoHyphens}/... when content_root is missing or stale. */
function workspaceForWsHexStoragePath(
  storagePath: string,
  workspaces: WorkspaceRouteInfo[]
): WorkspaceRouteInfo | undefined {
  const normalized = storagePath.replace(/^\/+/, '')
  const match = normalized.match(WS_STORAGE_HEX_PREFIX_RE)
  if (!match) return undefined
  const hex = match[1].toLowerCase()
  return workspaces.find((w) => workspaceIdToStorageHex(w.id) === hex)
}

function resolvedContentRootForWorkspace(ws: WorkspaceRouteInfo): string {
  if (ws.contentRoot?.trim()) {
    return normalizeContentRoot(ws.contentRoot)
  }
  return `ws/${workspaceIdToStorageHex(ws.id)}`
}

/** Find workspace whose content_root prefix matches a storage path. */
export function workspaceForStoragePath(
  storagePath: string,
  workspaces: WorkspaceRouteInfo[]
): WorkspaceRouteInfo | undefined {
  const normalized = storagePath.replace(/^\/+/, '')
  if (!normalized) return undefined
  let best: { ws: WorkspaceRouteInfo; len: number } | undefined
  for (const w of workspaces) {
    const root = resolvedContentRootForWorkspace(w)
    if (normalized === root || normalized.startsWith(`${root}/`)) {
      if (!best || root.length > best.len) {
        best = { ws: w, len: root.length }
      }
    }
  }
  if (best) return best.ws
  return workspaceForWsHexStoragePath(normalized, workspaces)
}

function storagePathRelativeToRoot(storagePath: string, contentRoot: string): string {
  const norm = storagePath.replace(/^\/+/, '')
  const root = normalizeContentRoot(contentRoot)
  if (norm === root) return ''
  if (norm.startsWith(`${root}/`)) return norm.slice(root.length + 1)
  return norm
}

function workspaceRootLabel(ws: WorkspaceRouteInfo): string {
  const name = ws.name?.trim()
  if (name) return name
  return ws.slug
}

export type DisplayPathSegment = {
  label: string
  storagePath: string
}

/**
 * Opaque cloud content roots look like `ws/{32hex}/...`.
 * These are internal storage keys - NEVER show them in user-facing UI copy.
 * Use {@link formatUserFacingStoragePath} / {@link displaySegmentsForStoragePath} instead.
 */
export function isOpaqueWsStoragePath(path: string | null | undefined): boolean {
  if (!path) return false
  return /^ws\/[a-f0-9]{32}\b/i.test(String(path).replace(/^\/+/, ''))
}

/** True when a single path segment is an opaque storage token (not user-facing). */
function isOpaqueStorageSegmentLabel(label: string): boolean {
  return (
    isOpaqueWsStoragePath(label) ||
    /^ws$/i.test(label) ||
    /^[a-f0-9]{32}$/i.test(label)
  )
}

/**
 * Dev-only guard: warn when opaque `ws/{32hex}` leaks into a user-visible string.
 * Call before rendering path copy if you build the string outside formatUserFacingStoragePath.
 */
export function assertNoOpaqueWsPathInUserCopy(text: string, context?: string): void {
  if (!import.meta.env.DEV) return
  if (/ws\/[a-f0-9]{32}/i.test(text) || (text.includes('ws') && /[a-f0-9]{32}/i.test(text))) {
    console.warn(
      `[Maater] Opaque ws/ storage path must never appear in user-facing UI${context ? ` (${context})` : ''}:`,
      text,
    )
  }
}

/**
 * USER-FACING PATH DISPLAY
 *
 * NEVER render raw `ws/{32hex}` (or any opaque content_root) in toasts, overlays,
 * breadcrumbs, drop targets, dialogs, or other user-visible chrome.
 * Storage paths stay for API/DB; display must use workspace name/slug + relative segments.
 *
 * Agents: if you add a UI string that includes a folder/file path, run it through this
 * helper (or displaySegmentsForStoragePath) before showing it.
 */
export function formatUserFacingStoragePath(
  storagePath: string | null | undefined,
  workspaces: WorkspaceRouteInfo[],
  emptyLabel = 'workspace root',
): string {
  const normalized = (storagePath || '').replace(/^\/+/, '').replace(/\/+$/, '')
  if (!normalized) return emptyLabel

  const segments = displaySegmentsForStoragePath(normalized, workspaces)
  const labels = segments
    .map((s) => s.label)
    .filter((label) => !isOpaqueStorageSegmentLabel(label))

  let result: string
  if (labels.length > 0) {
    result = labels.join(' / ')
  } else {
    // Last resort: strip opaque root and show relative tail only.
    const stripped = normalized.replace(/^ws\/[a-f0-9]{32}\/?/i, '')
    if (stripped && !isOpaqueWsStoragePath(stripped)) {
      result = stripped
        .split('/')
        .filter((part) => part && !isOpaqueStorageSegmentLabel(part))
        .join(' / ')
    } else {
      result = emptyLabel
    }
  }

  if (!result) result = emptyLabel
  assertNoOpaqueWsPathInUserCopy(result, 'formatUserFacingStoragePath')
  return result
}

/** Human-readable breadcrumb segments for a storage folder path (hides ws/{orgHex} prefix). */
export function displaySegmentsForStoragePath(
  storagePath: string,
  workspaces: WorkspaceRouteInfo[]
): DisplayPathSegment[] {
  const normalized = storagePath.replace(/^\/+/, '').replace(/\/+$/, '')
  if (!normalized) return []

  const ws = workspaceForStoragePath(normalized, workspaces)
  if (ws?.slug) {
    const root = resolvedContentRootForWorkspace(ws)
    const relative = storagePathRelativeToRoot(normalized, root)
    const segments: DisplayPathSegment[] = [{ label: workspaceRootLabel(ws), storagePath: root }]
    if (relative) {
      const parts = relative.split('/').filter(Boolean)
      let accum = root
      for (const part of parts) {
        accum = `${accum}/${part}`
        segments.push({ label: part, storagePath: accum })
      }
    }
    return segments
  }

  const parts = normalized.split('/').filter(Boolean)
  return parts.map((part, index) => ({
    label: part,
    storagePath: parts.slice(0, index + 1).join('/'),
  }))
}

/** Folder title for explorer toolbar / tab (last display segment). */
export function displayFolderTitleForStoragePath(
  storagePath: string,
  workspaces: WorkspaceRouteInfo[] = []
): string {
  const segments = displaySegmentsForStoragePath(storagePath, workspaces)
  if (segments.length === 0) return 'Explorer'
  return segments[segments.length - 1].label
}

/** Storage path -> slug-shaped path segment (no leading slash). */
export function storagePathToUrlPath(
  storagePath: string,
  workspaces: WorkspaceRouteInfo[]
): string {
  const fp = storagePath.replace(/^\/+/, '').replace(/\/+$/, '')
  if (!fp || workspaces.length === 0) return fp

  const ws =
    workspaceForStoragePath(fp, workspaces) ??
    (workspaces.length === 1 ? workspaces[0] : undefined)
  if (!ws?.slug) return fp

  const root = resolvedContentRootForWorkspace(ws)
  const relative = storagePathRelativeToRoot(fp, root)
  if (relative === '') return ws.slug
  return `${ws.slug}/${relative}`
}

/**
 * Clipboard / "Copy path" value: always slug-shaped (`{slug}/...`), never `ws/{32hex}/...`.
 * Prefer this over raw storage paths and over display labels with ` / ` separators.
 */
export function userFacingCopyPath(
  storagePath: string | null | undefined,
  workspaces: WorkspaceRouteInfo[],
): string | null {
  const fp = (storagePath || '').replace(/^\/+/, '').replace(/\/+$/, '')
  if (!fp) return null

  const slugPath = storagePathToUrlPath(fp, workspaces)
  if (slugPath && !isOpaqueWsStoragePath(slugPath) && !/^ws\//i.test(slugPath)) {
    assertNoOpaqueWsPathInUserCopy(slugPath, 'userFacingCopyPath')
    return slugPath
  }

  const stripped = fp.replace(/^ws\/[a-f0-9]{32}\/?/i, '')
  if (stripped && !isOpaqueWsStoragePath(stripped) && !/^ws\//i.test(stripped)) {
    assertNoOpaqueWsPathInUserCopy(stripped, 'userFacingCopyPath')
    return stripped
  }

  return null
}

/** Match workspace by URL first segment (slug or display name). */
function workspaceForUrlFirstSegment(
  first: string,
  workspaces: WorkspaceRouteInfo[]
): WorkspaceRouteInfo | undefined {
  if (!first) return undefined
  const bySlug = workspaces.find((w) => w.slug === first)
  if (bySlug) return bySlug
  const lower = first.toLowerCase()
  const byAlias = workspaces.find((w) =>
    slugAliasesForWorkspace(w).some((alias) => alias.toLowerCase() === lower)
  )
  if (byAlias) return byAlias
  return workspaces.find((w) => {
    const name = w.name?.trim()
    if (!name) return false
    return name === first || name.toLowerCase() === lower
  })
}

/** Slug- or display-name-shaped browser path -> storage path for API / DB lookups. */
export function urlPathToStoragePath(
  urlPath: string,
  workspaces: WorkspaceRouteInfo[]
): string {
  const normalized = urlPath.replace(/^\/+/, '').replace(/\/+$/, '')
  if (!normalized || workspaces.length === 0) return normalized

  const segments = normalized.split('/')
  const first = segments[0]
  const ws = workspaceForUrlFirstSegment(first, workspaces)
  // Always map to content_root - never keep display name as a storage prefix.
  if (!ws) return normalized

  const root = resolvedContentRootForWorkspace(ws)
  const rest = segments.slice(1).join('/')
  if (rest === '') return root
  return `${root}/${rest}`
}

/**
 * Resolve workspace id from a document URL/storage path without fetching.
 * Handles slug roots (`flownotes-product/...`), storage roots, and display-name
 * roots (`FlowNotes Product/...`) so deep links can enable the target workspace
 * before by-path.
 */
export function workspaceIdForDocumentUrlPath(
  pathOrId: string,
  workspaces: WorkspaceRouteInfo[]
): string | undefined {
  const trimmed = pathOrId.replace(/^\/+/, '').replace(/\/+$/, '')
  if (!trimmed || workspaces.length === 0 || isUuid(trimmed)) return undefined

  const first = trimmed.split('/')[0]
  const bySegment = workspaceForUrlFirstSegment(first, workspaces)
  if (bySegment) return bySegment.id

  const storage = resolveStoragePathForFetch(trimmed, workspaces)
  return workspaceForStoragePath(storage, workspaces)?.id
}

/** Normalize explorer folder paths (sidebar tree, URL restore, API folder=) to storage paths. */
export function normalizeFolderStoragePath(
  folderPath: string | null | undefined,
  workspaces: WorkspaceRouteInfo[] = []
): string | null {
  if (folderPath == null || folderPath === '') return folderPath ?? null
  const trimmed = folderPath.replace(/^\/+/, '').replace(/\/+$/, '')
  if (!trimmed) return null
  if (workspaces.length === 0) return trimmed
  return urlPathToStoragePath(trimmed, workspaces)
}

/** Redirect /ws/{orgIdHex}/... bookmarks to /{slug}/... */
export function resolveWsOpaqueStoragePath(
  pathname: string,
  workspaces: WorkspaceRouteInfo[]
): string | null {
  const match = pathname.match(WS_OPAQUE_URL_RE)
  if (!match) return null
  const hexId = match[1]
  const tail = match[2]?.replace(/^\/+/, '') ?? ''
  const ws = workspaces.find((w) => {
    if (!w.contentRoot) return false
    return normalizeContentRoot(w.contentRoot) === `ws/${hexId}`
  })
  if (!ws?.slug) return null
  if (!tail) return `/${encodeURIComponent(ws.slug)}`
  return `/${encodeURIComponent(ws.slug)}/${tail
    .split('/')
    .map((s) => encodeURIComponent(s))
    .join('/')}`
}

export function isUuid(value: string): boolean {
  return UUID_RE.test(value)
}

/**
 * Bare document id from a pasted path or `/doc/{uuid}` stem.
 * `uuid.md` / `uuid.html` are not storage paths - do not by-path probe them.
 */
export function documentUuidFromPathOrId(pathOrId: string): string | null {
  const trimmed = String(pathOrId || '').replace(/^\/+/, '').trim()
  if (!trimmed) return null
  const docPrefix = trimmed.match(/^doc\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i)
  if (docPrefix && isUuid(docPrefix[1])) return docPrefix[1]
  if (trimmed.includes('/')) return null
  const stem = trimmed.replace(/\.(md|html)$/i, '')
  return isUuid(stem) ? stem : null
}

export type UrlIntent =
  | { type: 'home' }
  | { type: 'edit-history' } // legacy; parseAppPath redirects to search view=activity
  | { type: 'inbox' }
  | { type: 'flow-studio'; surface: 'pulse' | 'gallery' | 'sources' | 'composer' }
  | { type: 'inbox-thread'; threadId: string }
  | { type: 'chat-conversation'; threadId: string }
  | { type: 'search'; view?: string; q?: string; path?: string; user?: string; semantic?: boolean }
  | { type: 'person'; userId: string; workspaceSlug?: string; workspaceId?: string }
  | { type: 'people-index'; workspaceSlug?: string; workspaceId?: string }
  | { type: 'settings'; panel?: string }
  | { type: 'document'; pathOrId: string; isLegacyDocRoute?: boolean }
  | { type: 'explorer'; folderPath: string | null }
  | { type: 'folder-entity'; folderId: string }
  | { type: 'workspace-home'; workspaceSlug: string }
  | {
      type: 'extension-basecamp'
      workspaceSlug: string
      strippedPath: string
      workspaceId?: string
    }
  | { type: 'extension'; workspaceSlug: string; extensionId: string; restPath: string }
  | { type: 'legacy-redirect'; targetPath: string }
  | { type: 'unknown' }

/** Browser path when opening a document by storage path or UUID. */
export function documentUrlForPathOrId(
  pathOrId: string,
  workspaces: WorkspaceRouteInfo[] = []
): string {
  if (isUuid(pathOrId)) {
    return `/doc/${encodeURIComponent(pathOrId)}`
  }
  const fp = pathOrId.replace(/^\/+/, '')
  if (workspaces.length > 0) {
    return pathToUrl(storagePathToUrlPath(fp, workspaces))
  }
  return pathToUrl(fp)
}

/** Full shareable URL (origin + app pathname) for clipboard. */
export function absoluteAppUrl(pathname: string): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`
  if (typeof window === 'undefined') return path
  return `${window.location.origin}${path}`
}

export function absoluteDocumentUrlForPathOrId(
  pathOrId: string,
  workspaces: WorkspaceRouteInfo[] = []
): string {
  return absoluteAppUrl(documentUrlForPathOrId(pathOrId, workspaces))
}

/** Strip trailing .md / .html for browser URL (longest match first). */
export function pathToUrl(filePath: string): string {
  const normalized = filePath.replace(/\\/g, '/').replace(/^\/+/, '')
  if (!normalized) return '/'
  for (const ext of STRIP_EXTENSIONS) {
    if (normalized.endsWith(ext)) {
      const base = normalized.slice(0, -ext.length)
      return `/${base.split('/').map(encodeURIComponent).join('/')}`
    }
  }
  return `/${normalized.split('/').map(encodeURIComponent).join('/')}`
}

/** Pathname (no query) to content-root path without leading slash; may still lack extension. */
export function pathnameToContentPath(pathname: string): string {
  const raw = pathname.replace(/^\/+/, '')
  if (!raw) return ''
  return raw.split('/').map((s) => decodeURIComponent(s)).join('/')
}

/**
 * Strip absolute / home-relative materialized workspace prefixes so by-path
 * uses documents.file_path (content-root relative).
 * e.g. /Users/x/.flownotes/tmp/hop-studios/a.md -> hop-studios/a.md
 */
export function stripMaterializedTmpPrefix(pathLike: string): string {
  let p = String(pathLike).replace(/\\/g, '/')
  if (/^file:\/\//i.test(p)) {
    try {
      p = decodeURIComponent(p.replace(/^file:\/\//i, ''))
    } catch {
      p = p.replace(/^file:\/\//i, '')
    }
  }
  p = p.replace(/^~(?=\/)/, '')
  const marker = '.flownotes/tmp/'
  const idx = p.toLowerCase().indexOf(marker)
  if (idx !== -1) {
    return p.slice(idx + marker.length).replace(/^\/+/, '')
  }
  return p.replace(/^\/+/, '')
}

/** Slug-shaped browser path -> storage path before document API fetch (ADR-0012). */
export function resolveStoragePathForFetch(
  pathOrId: string,
  workspaces: WorkspaceRouteInfo[] = []
): string {
  const trimmed = stripMaterializedTmpPrefix(pathOrId).replace(/\/+$/, '')
  if (!trimmed || !trimmed.includes('/') || workspaces.length === 0) return trimmed
  return urlPathToStoragePath(trimmed, workspaces)
}

/** Probe candidates for cold-load document resolve. */
export function storagePathProbeCandidates(contentPath: string): string[] {
  const normalized = stripMaterializedTmpPrefix(contentPath)
  if (!normalized) return []
  if (documentUuidFromPathOrId(normalized)) return []
  const lower = normalized.toLowerCase()
  if (hasStoredDocExtension(normalized)) {
    return [normalized]
  }
  // Extensionless path: file.md, then folder-hub README.
  // Do not probe the raw folder path - that 404-spams leaf folders (todos/).
  const candidates = [`${normalized}.md`, `${normalized}.html`]
  if (!lower.endsWith('/readme') && !lower.endsWith('/index')) {
    candidates.push(`${normalized}/README.md`, `${normalized}/readme.md`)
  }
  return candidates
}

export function workspaceSlugForId(
  workspaces: WorkspaceRouteInfo[],
  workspaceId?: string | null
): string | undefined {
  if (!workspaceId) return undefined
  return workspaces.find((w) => w.id === workspaceId)?.slug
}

export function workspaceIdForSlug(
  workspaces: WorkspaceRouteInfo[],
  slug: string
): string | undefined {
  return workspaces.find((w) => w.slug === slug)?.id
}

export function basecampRoutePrefixFromSlug(workspaceSlug?: string | null): string {
  return workspaceSlug
    ? `/${encodeURIComponent(workspaceSlug)}/basecamp`
    : '/basecamp'
}

/** Prefer slug stored on the tab (from deep link) over workspaceId lookup. */
export function basecampPrefixForTab(
  tab: OpenTab,
  workspaces: WorkspaceRouteInfo[] = []
): string {
  const slugFromMeta = tab.integrationData?.workspaceSlug
  const slugFromId =
    tab.workspaceId != null ? workspaceSlugForId(workspaces, tab.workspaceId) : undefined
  return basecampRoutePrefixFromSlug(slugFromMeta ?? slugFromId ?? null)
}

/** /{workspaceSlug}/basecamp/... or /{slug}/project/... shorthand -> /basecamp/... tail */
export function normalizeBasecampPathname(pathname: string): string {
  const slugged = pathname.match(/^\/([^/]+)\/basecamp(\/.*)?$/)
  if (slugged) {
    const first = decodeURIComponent(slugged[1])
    if (!BASECAMP_SEGMENT_LITERALS.has(first)) {
      return `/basecamp${slugged[2] ?? ''}`
    }
    return pathname
  }

  const todoShorthand = pathname.match(/^\/([^/]+)\/project\/(\d+)\/todo\/(\d+)$/)
  if (todoShorthand) {
    const first = decodeURIComponent(todoShorthand[1])
    if (!BASECAMP_SEGMENT_LITERALS.has(first)) {
      return `/basecamp/project/${todoShorthand[2]}/todo/${todoShorthand[3]}`
    }
  }

  const listShorthand = pathname.match(/^\/([^/]+)\/project\/(\d+)\/todo-list\/(\d+)$/)
  if (listShorthand) {
    const first = decodeURIComponent(listShorthand[1])
    if (!BASECAMP_SEGMENT_LITERALS.has(first)) {
      return `/basecamp/project/${listShorthand[2]}/todo-list/${listShorthand[3]}`
    }
  }

  const projectShorthand = pathname.match(/^\/([^/]+)\/project\/(\d+)$/)
  if (projectShorthand) {
    const first = decodeURIComponent(projectShorthand[1])
    if (!BASECAMP_SEGMENT_LITERALS.has(first)) {
      return `/basecamp/project/${projectShorthand[2]}`
    }
  }

  return pathname
}

/** True when two paths are the same Basecamp integration route (workspace slug prefix optional). */
export function basecampRoutesEquivalent(pathA: string, pathB: string): boolean {
  const na = normalizeBasecampPathname(pathA)
  const nb = normalizeBasecampPathname(pathB)
  if (!na.startsWith('/basecamp') || !nb.startsWith('/basecamp')) return false
  return na === nb
}

/** Same Basecamp place, ignoring share-link query. */
export function basecampPathnamesEquivalent(pathA: string, pathB: string): boolean {
  const strip = (value: string) => {
    const i = value.indexOf('?')
    return i >= 0 ? value.slice(0, i) : value
  }
  return basecampRoutesEquivalent(strip(pathA), strip(pathB))
}

export function buildFolderUrl(
  folder: {
    id?: string | null
    path?: string | null
  },
  workspaces: WorkspaceRouteInfo[] = []
): string {
  if (folder.id && isUuid(folder.id)) {
    return `/folder/${encodeURIComponent(folder.id)}`
  }
  if (folder.path) {
    const storagePath = folder.path.endsWith('/') ? folder.path.slice(0, -1) : folder.path
    const urlPath =
      workspaces.length > 0 ? storagePathToUrlPath(storagePath, workspaces) : storagePath
    return pathToUrl(urlPath)
  }
  return '/'
}

export type BuildAppUrlInput = {
  tab: OpenTab
  workspaces?: WorkspaceRouteInfo[]
  searchParams?: URLSearchParams
}

/** Extensionless URL for a stored content document (note, todo, work-spec, etc.). */
export function buildStoredDocumentUrl(
  tab: {
    id: string
    filePath?: string | null
  },
  workspaces: WorkspaceRouteInfo[] = []
): string {
  const rawFp = tab.filePath || (tab.id.includes('/') ? tab.id : null)
  // A cold `/doc/{uuid}` tab stores the UUID in filePath until fetch. That is not a storage path.
  // Treating it as one rewrites the share URL to `/{uuid}` or `/{only-slug}/{uuid}`, then Home.
  const fp = rawFp && !isUuid(rawFp) ? rawFp : null
  if (fp) {
    const urlPath = workspaces.length > 0 ? storagePathToUrlPath(fp, workspaces) : fp
    return pathToUrl(urlPath)
  }
  if (isUuid(tab.id)) return `/doc/${encodeURIComponent(tab.id)}`
  return pathToUrl(tab.id)
}

/**
 * `/doc/{uuid}` is only the bar while filePath is still the uuid.
 * Once the open tab has a storage path, return the folder URL to replace it.
 * Null until that path exists, or when the bar is already the folder URL.
 */
export function folderUrlForResolvedDocUuidBar(
  pathname: string,
  tab: { id: string; filePath?: string | null } | undefined,
  workspaces: WorkspaceRouteInfo[] = [],
): string | null {
  if (!tab || !pathname.startsWith('/doc/')) return null
  let rest = pathname.slice('/doc/'.length)
  try {
    rest = rest.split('/').map((segment) => decodeURIComponent(segment)).join('/')
  } catch {
    return null
  }
  if (!isUuid(rest) || tab.id !== rest) return null
  const fp = (tab.filePath ?? '').replace(/^\/+/, '')
  if (!fp.includes('/') || isUuid(fp)) return null
  const vanity = buildStoredDocumentUrl(tab, workspaces)
  const vanityPath = vanity.split('?')[0] || vanity
  if (!vanityPath || vanityPath === pathname || vanityPath.startsWith('/doc/')) return null
  return vanityPath
}

export function buildAppUrl({ tab, workspaces = [] }: BuildAppUrlInput): string {
  const wsSlug = workspaceSlugForId(workspaces, tab.workspaceId)
  const bcPre = basecampPrefixForTab(tab, workspaces)

  switch (tab.type) {
    case 'home':
      return '/'
    case 'edit-history':
      return '/search?view=activity'
    case 'inbox':
      return '/inbox'
    case 'flow-index': {
      const raw = (tab.id || '').startsWith('flow-studio:')
        ? tab.id.slice('flow-studio:'.length)
        : 'pulse'
      const surface =
        raw === 'gallery' || raw === 'sources' || raw === 'composer' || raw === 'pulse'
          ? raw
          : 'pulse'
      return `/flow-studio/${surface}`
    }
    case 'integration-google-drive':
      return '/drive'
    case 'integration-nextcloud':
      return '/nextcloud'
    case 'inbox-thread': {
      const tid = tab.inboxThreadId ?? tab.id.replace(/^inbox-thread-/, '')
      return tid ? `/inbox/thread/${encodeURIComponent(tid)}` : '/inbox'
    }
    case 'chat-conversation': {
      const tid = tab.chatThreadId ?? tab.id.replace(/^chat-conversation-/, '')
      return tid ? `/chat/thread/${encodeURIComponent(tid)}` : '/'
    }
    case 'search-view': {
      if (tab.searchNamedView === 'activity') {
        const params = new URLSearchParams({ view: 'activity' })
        const user = (tab.searchUpdatedBy || '').trim()
        if (user) params.set('user', user)
        return `/search?${params.toString()}`
      }
      const params = new URLSearchParams()
      const q = (tab.initialQuery || '').trim()
      if (q) params.set('q', q)
      const path = (tab.searchPath || '').trim()
      if (path) params.set('path', path)
      if (tab.searchSemantic) params.set('semantic', '1')
      const qs = params.toString()
      return qs ? `/search?${qs}` : '/search'
    }
    case 'person-view': {
      const userId = tab.personUserId || personUserIdFromTabId(tab.id)
      const peopleBase = wsSlug
        ? `/${encodeURIComponent(wsSlug)}/people`
        : '/people'
      return userId ? `${peopleBase}/${encodeURIComponent(userId)}` : peopleBase
    }
    case 'workspace-home':
      return wsSlug ? `/${encodeURIComponent(wsSlug)}` : '/'
    case 'desk-home': {
      const deskPath = tab.filePath
      if (!deskPath) return wsSlug ? `/${encodeURIComponent(wsSlug)}` : '/'
      const storagePath = deskPath.endsWith('/') ? deskPath.slice(0, -1) : deskPath
      const urlPath =
        workspaces.length > 0 ? storagePathToUrlPath(storagePath, workspaces) : storagePath
      return pathToUrl(urlPath)
    }
    case 'explorer': {
      const folderPath = tab.explorerData?.folderPath
      if (!folderPath) return wsSlug ? `/${encodeURIComponent(wsSlug)}` : '/'
      const storagePath = folderPath.endsWith('/') ? folderPath.slice(0, -1) : folderPath
      const urlPath =
        workspaces.length > 0 ? storagePathToUrlPath(storagePath, workspaces) : storagePath
      const path = pathToUrl(urlPath)
      const q = (tab.explorerData?.panelSearchQuery || '').trim()
      return q ? appendQuery(path, new URLSearchParams({ q })) : path
    }
    case 'work-spec':
    case 'note':
    case 'voice-memo':
    case 'todo':
    case 'meeting':
    case 'idea':
    case 'research':
      return buildStoredDocumentUrl(tab, workspaces)
    case 'integration-basecamp': {
      const pre = bcPre
      const section = resolveBasecampIndexSection(tab)
      if (section === 'bookmarks') return `${pre}/bookmarks`
      if (section === 'mystuff') return `${pre}/mystuff`
      if (section === 'todos') {
        return appendQuery(
          `${pre}/todos`,
          todosQueryFromStorage(tab.workspaceId || tab.integrationData?.organizationId),
        )
      }
      if (section === 'projects') return `${pre}/projects`
      if (section === 'activity') {
        return appendQuery(
          `${pre}/activity`,
          activityQueryFromStorage(tab.workspaceId || tab.integrationData?.organizationId),
        )
      }
      if (tab.integrationData?.projectId) {
        return `${pre}/project/${tab.integrationData.projectId}`
      }
      return pre
    }
    case 'integration-basecamp-todo-list': {
      const pre = bcPre
      if (tab.integrationData?.projectId && tab.integrationData?.listId) {
        return `${pre}/project/${tab.integrationData.projectId}/todo-list/${tab.integrationData.listId}`
      }
      if (tab.integrationData?.projectId) {
        const listId = tab.id.replace('basecamp-todo-list-', '')
        if (listId && listId !== tab.id) {
          return `${pre}/project/${tab.integrationData.projectId}/todo-list/${listId}`
        }
        return `${pre}/project/${tab.integrationData.projectId}`
      }
      return pre
    }
    case 'integration-basecamp-todo': {
      const pre = bcPre
      if (tab.integrationData?.projectId && tab.integrationData?.todoId) {
        return `${pre}/project/${tab.integrationData.projectId}/todo/${tab.integrationData.todoId}`
      }
      if (tab.integrationData?.projectId) {
        const todoId = tab.id.replace('basecamp-todo-', '')
        if (todoId && todoId !== tab.id) {
          return `${pre}/project/${tab.integrationData.projectId}/todo/${todoId}`
        }
        return `${pre}/project/${tab.integrationData.projectId}`
      }
      return pre
    }
    case 'integration-basecamp-triage':
      return wsSlug ? `/${encodeURIComponent(wsSlug)}/basecamp/triage` : '/basecamp/triage'
    case 'integration-voice-memos':
      return wsSlug ? `/${encodeURIComponent(wsSlug)}/voice-memos` : '/voice-memos'
    case 'integration-product-board':
      return wsSlug ? `/${encodeURIComponent(wsSlug)}/product-board` : '/product-board'
    case 'integration-google-drive-preview': {
      const fileId = tab.integrationData?.driveFileId ?? tab.id.replace(/^gdrive-preview-/, '')
      return fileId ? `/drive/preview/${encodeURIComponent(fileId)}` : '/drive'
    }
    case 'platform-skill':
      // Git-managed; no document URL until Skills-as-Documents sync exists.
      return '/'
    default: {
      if (tab.filePath || isUuid(tab.id) || tab.id.includes('/')) {
        return buildStoredDocumentUrl(tab, workspaces)
      }
      return '/'
    }
  }
}

export function absoluteAppUrlForTab(
  tab: OpenTab,
  workspaces: WorkspaceRouteInfo[] = []
): string {
  return absoluteAppUrl(buildAppUrl({ tab, workspaces }))
}

/**
 * Rewrite /{legacySlug}/... to /{currentSlug}/... once a workspace slug has changed.
 * No-op while the legacy slug is still the current slug.
 */
function canonicalLegacySlugPath(
  pathname: string,
  workspaces: WorkspaceRouteInfo[]
): string | null {
  const trimmed = pathname.replace(/^\/+/, '').replace(/\/+$/, '')
  if (!trimmed || workspaces.length === 0) return null
  const segments = trimmed.split('/')
  let first = segments[0]
  try {
    first = decodeURIComponent(first)
  } catch {
    /* keep raw */
  }
  const ws = workspaceForUrlFirstSegment(first, workspaces)
  if (!ws?.slug || ws.slug === first) return null
  const lower = first.toLowerCase()
  const isAlias = slugAliasesForWorkspace(ws).some((alias) => alias.toLowerCase() === lower)
  if (!isAlias) return null
  const rest = segments
    .slice(1)
    .map((segment) => {
      try {
        return encodeURIComponent(decodeURIComponent(segment))
      } catch {
        return encodeURIComponent(segment)
      }
    })
    .join('/')
  const target = rest ? `/${encodeURIComponent(ws.slug)}/${rest}` : `/${encodeURIComponent(ws.slug)}`
  return target !== pathname ? target : null
}

/**
 * Legacy pathname -> canonical v2 path (no query). Returns null if no redirect.
 */
export function resolveLegacyPath(
  pathname: string,
  workspaces: WorkspaceRouteInfo[] = []
): string | null {
  const wsOpaque = resolveWsOpaqueStoragePath(pathname, workspaces)
  if (wsOpaque && wsOpaque !== pathname) return wsOpaque

  if (pathname.startsWith('/doc/')) {
    const rest = pathname.slice('/doc/'.length)
    const decoded = rest.split('/').map((s) => decodeURIComponent(s)).join('/')
    if (isUuid(decoded)) return `/doc/${encodeURIComponent(decoded)}`
    const urlPath =
      workspaces.length > 0 ? storagePathToUrlPath(decoded, workspaces) : decoded
    return pathToUrl(urlPath)
  }

  const exploreMatch = pathname.match(/^\/explore(?:\/(.*))?$/)
  if (exploreMatch) {
    const tail = exploreMatch[1]
    if (!tail) return '/'
    const folderPath = tail.split('/').map((s) => decodeURIComponent(s)).join('/')
    const urlPath =
      workspaces.length > 0 ? storagePathToUrlPath(folderPath, workspaces) : folderPath
    return pathToUrl(urlPath)
  }

  const workspaceMatch = pathname.match(/^\/workspace\/([^/]+)$/)
  if (workspaceMatch) {
    const wsId = decodeURIComponent(workspaceMatch[1])
    const slug = workspaceSlugForId(workspaces, wsId)
    if (slug) return `/${encodeURIComponent(slug)}`
    return null
  }

  if (pathname === '/voice-memos' || pathname.startsWith('/voice-memos/')) {
    const slug = workspaces[0]?.slug
    if (slug) return `/${encodeURIComponent(slug)}${pathname}`
    return null
  }

  if (pathname === '/product-board' || pathname.startsWith('/product-board/')) {
    const slug = workspaces[0]?.slug
    if (slug) return `/${encodeURIComponent(slug)}${pathname}`
    return null
  }

  if (pathname.startsWith('/basecamp')) {
    const { workspaceId, strippedPath } = stripOptionalBasecampWorkspaceSegment(pathname)
    let slug: string | undefined
    if (workspaceId) {
      slug = workspaceSlugForId(workspaces, workspaceId)
    }
    if (!slug) {
      try {
        const pending = sessionStorage.getItem('flownotes_basecamp_workspace_slug')
        if (
          pending &&
          (workspaces.length === 0 || workspaces.some((w) => w.slug === pending))
        ) {
          slug = pending
        }
      } catch {
        /* private mode */
      }
    }
    // Do not guess workspaces[0] — wrong slug (e.g. flownotes-product vs hop-studios).
    if (!slug) return null
    const tail = strippedPath === '/basecamp' ? '/basecamp' : strippedPath.slice('/basecamp'.length)
    return `/${encodeURIComponent(slug)}${tail || '/basecamp'}`
  }

  // Shorthand: /{slug}/project/:id/todo/:id (missing /basecamp/) — otherwise parsed as a document path.
  const bcTodoShorthand = pathname.match(/^\/([^/]+)\/project\/(\d+)\/todo\/(\d+)$/)
  if (bcTodoShorthand) {
    const [, slug, projectId, todoId] = bcTodoShorthand
    return `/${encodeURIComponent(slug)}/basecamp/project/${projectId}/todo/${todoId}`
  }
  const bcTodoListShorthand = pathname.match(/^\/([^/]+)\/project\/(\d+)\/todo-list\/(\d+)$/)
  if (bcTodoListShorthand) {
    const [, slug, projectId, listId] = bcTodoListShorthand
    return `/${encodeURIComponent(slug)}/basecamp/project/${projectId}/todo-list/${listId}`
  }
  const bcProjectShorthand = pathname.match(/^\/([^/]+)\/project\/(\d+)$/)
  if (bcProjectShorthand) {
    const [, slug, projectId] = bcProjectShorthand
    return `/${encodeURIComponent(slug)}/basecamp/project/${projectId}`
  }

  if (
    pathname === '/view:edit-history' ||
    pathname === '/view%3Aedit-history' ||
    pathname === '/edit-history'
  ) {
    return '/search?view=activity'
  }

  const aliasRedirect = canonicalLegacySlugPath(pathname, workspaces)
  if (aliasRedirect) return aliasRedirect

  const contentPath = pathnameToContentPath(pathname)
  if (
    contentPath &&
    (contentPath.toLowerCase().endsWith('.md') || contentPath.toLowerCase().endsWith('.html'))
  ) {
    const storagePath =
      workspaces.length > 0 ? urlPathToStoragePath(contentPath, workspaces) : contentPath
    const urlPath =
      workspaces.length > 0 ? storagePathToUrlPath(storagePath, workspaces) : storagePath
    const canonical = pathToUrl(urlPath)
    if (canonical !== pathname) return canonical
  }

  return null
}

/** Canonical extensionless browser path for a document URL (ADR-0012). */
export function canonicalDocumentPathname(
  pathname: string,
  workspaces: WorkspaceRouteInfo[] = [],
): string {
  const intent = parseAppPath(pathname, new URLSearchParams(), workspaces)
  if (intent.type === 'legacy-redirect') {
    return canonicalDocumentPathname(intent.targetPath, workspaces)
  }
  if (intent.type !== 'document') return pathname
  const urlPath =
    workspaces.length > 0
      ? storagePathToUrlPath(intent.pathOrId, workspaces)
      : intent.pathOrId
  return pathToUrl(urlPath)
}

/** True when two browser paths refer to the same stored document (ignores .md/.html in bar). */
export function documentUrlPathsEquivalent(
  pathnameA: string,
  pathnameB: string,
  workspaces: WorkspaceRouteInfo[] = [],
): boolean {
  if (pathnameA === pathnameB) return true
  if (basecampRoutesEquivalent(pathnameA, pathnameB)) return true
  const aIntent = parseAppPath(pathnameA, new URLSearchParams(), workspaces)
  const bIntent = parseAppPath(pathnameB, new URLSearchParams(), workspaces)
  const aDoc =
    aIntent.type === 'document'
      ? aIntent
      : aIntent.type === 'legacy-redirect' &&
          parseAppPath(aIntent.targetPath, new URLSearchParams(), workspaces).type ===
            'document'
        ? (parseAppPath(aIntent.targetPath, new URLSearchParams(), workspaces) as {
            type: 'document'
            pathOrId: string
          })
        : null
  const bDoc =
    bIntent.type === 'document'
      ? bIntent
      : bIntent.type === 'legacy-redirect' &&
          parseAppPath(bIntent.targetPath, new URLSearchParams(), workspaces).type ===
            'document'
        ? (parseAppPath(bIntent.targetPath, new URLSearchParams(), workspaces) as {
            type: 'document'
            pathOrId: string
          })
        : null
  if (!aDoc || !bDoc) return false
  return (
    canonicalDocumentPathname(pathnameA, workspaces) ===
    canonicalDocumentPathname(pathnameB, workspaces)
  )
}

/** Match keys for document path/id (storage paths with extension variants). */
export function documentPathMatchKeys(
  pathOrId: string,
  workspaces: WorkspaceRouteInfo[] = [],
): Set<string> {
  const trimmed = pathOrId.replace(/^\/+/, '').replace(/\/+$/, '')
  const storage = trimmed.includes('/')
    ? resolveStoragePathForFetch(trimmed, workspaces)
    : trimmed
  const keys = new Set<string>()
  const addPathKeys = (path: string) => {
    for (const candidate of storagePathProbeCandidates(path)) {
      keys.add(candidate)
      keys.add(candidate.replace(/\.(md|html)$/i, ''))
    }
    keys.add(path)
    keys.add(path.replace(/\.(md|html)$/i, ''))
  }
  addPathKeys(storage)
  // Also key the original slug/display form so provisional cold-load tabs match.
  if (trimmed && trimmed !== storage) {
    addPathKeys(trimmed)
  }
  if (workspaces.length > 0 && storage.includes('/')) {
    const slugShaped = storagePathToUrlPath(storage, workspaces)
    if (slugShaped && slugShaped !== storage && slugShaped !== trimmed) {
      addPathKeys(slugShaped)
    }
  }
  if (isUuid(trimmed)) keys.add(trimmed)
  return keys
}

/** Find an open tab for a document path, UUID, or legacy id (matches filePath). */
export function findOpenTabForDocumentPath(
  openTabs: OpenTab[],
  pathOrId: string,
  workspaces: WorkspaceRouteInfo[] = [],
): OpenTab | undefined {
  const keys = documentPathMatchKeys(pathOrId, workspaces)
  return openTabs.find((tab) => {
    if (keys.has(tab.id)) return true
    const fp = tab.filePath?.replace(/^\/+/, '')
    if (!fp) return false
    if (keys.has(fp)) return true
    return keys.has(fp.replace(/\.(md|html)$/i, ''))
  })
}

/**
 * Home, an inbox thread, and a chat tab are not the document. A warm in-app click
 * leaves that tab active while the bar already shows the document URL. Tab-to-URL
 * must yield, or it writes the reader URL back and the document flashes away.
 */
export function tabYieldsToDocumentDeepLink(
  activeTabId: string | null,
  tab: { id?: string; type?: string } | undefined,
): boolean {
  const id = activeTabId || tab?.id || ''
  const type = tab?.type || ''
  return (
    id === 'home' ||
    type === 'home' ||
    id.startsWith('inbox-thread-') ||
    type === 'inbox-thread' ||
    id.startsWith('chat-conversation-') ||
    type === 'chat-conversation'
  )
}

/**
 * True when the address bar carries an explicit navigation intent that must win over
 * localStorage-restored tab state on cold load (shared links, bookmarks, refresh).
 */
export function shouldDeferTabUrlSyncToAddressBar(
  pathname: string,
  searchParams: URLSearchParams,
  workspaces: WorkspaceRouteInfo[],
  activeTab: OpenTab | undefined,
): boolean {
  if (isDocumentDeepLinkRestorePending()) {
    return true
  }
  const intent = parseAppPath(pathname, searchParams, workspaces)
  if (intent.type === 'home' || intent.type === 'unknown') {
    return false
  }
  const tabUrl = activeTab ? buildAppUrl({ tab: activeTab, workspaces }) : '/'
  if (intent.type === 'legacy-redirect') {
    const targetIntent = parseAppPath(intent.targetPath, searchParams, workspaces)
    if (targetIntent.type === 'document') {
      return !documentUrlPathsEquivalent(intent.targetPath, tabUrl, workspaces)
    }
    return false
  }
  if (intent.type === 'document') {
    return !documentUrlPathsEquivalent(pathname, tabUrl, workspaces)
  }
  if (basecampRoutesEquivalent(pathname, tabUrl)) return true
  return pathname !== tabUrl
}

export function parseAppPath(
  pathname: string,
  searchParams: URLSearchParams,
  workspaces: WorkspaceRouteInfo[] = []
): UrlIntent {
  const legacy = resolveLegacyPath(pathname, workspaces)
  if (legacy && legacy !== pathname) {
    return { type: 'legacy-redirect', targetPath: legacy }
  }

  if (pathname === '/' || pathname === '') {
    if (searchParams.get('settings')) {
      return { type: 'settings', panel: searchParams.get('settings') ?? undefined }
    }
    return { type: 'home' }
  }

  if (pathname === '/inbox') return { type: 'inbox' }

  const flowStudio = pathname.match(/^\/flow-studio\/(pulse|gallery|sources|composer)\/?$/)
  if (flowStudio) {
    const surface = flowStudio[1]
    if (
      surface === 'pulse' ||
      surface === 'gallery' ||
      surface === 'sources' ||
      surface === 'composer'
    ) {
      return { type: 'flow-studio', surface }
    }
  }

  if (pathname === '/drive') {
    return { type: 'extension', workspaceSlug: '', extensionId: 'google-drive', restPath: '' }
  }

  if (pathname === '/nextcloud') {
    return { type: 'extension', workspaceSlug: '', extensionId: 'nextcloud', restPath: '' }
  }

  const inboxThread = pathname.match(/^\/inbox\/thread\/([^/]+)$/)
  if (inboxThread) {
    return { type: 'inbox-thread', threadId: decodeURIComponent(inboxThread[1]) }
  }

  const chatThread = pathname.match(/^\/chat\/thread\/([^/]+)$/)
  if (chatThread) {
    return { type: 'chat-conversation', threadId: decodeURIComponent(chatThread[1]) }
  }

  if (pathname === '/search') {
    const view = searchParams.get('view') ?? undefined
    const q = searchParams.get('q') ?? undefined
    const path = searchParams.get('path') ?? undefined
    const user = searchParams.get('user') ?? undefined
    const semantic = searchParams.get('semantic') === '1'
    return {
      type: 'search',
      ...(view ? { view } : {}),
      ...(q ? { q } : {}),
      ...(path ? { path } : {}),
      ...(user ? { user } : {}),
      ...(semantic ? { semantic: true } : {}),
    }
  }

  if (pathname === '/people') {
    return { type: 'people-index' }
  }

  const peopleMatch = pathname.match(/^\/people\/([^/]+)$/)
  if (peopleMatch) {
    return { type: 'person', userId: decodeURIComponent(peopleMatch[1]) }
  }

  // Turfed: Activity / All edits lives on Search as view=activity
  if (pathname === '/edit-history') {
    const user = searchParams.get('user')
    const qs = new URLSearchParams({ view: 'activity' })
    if (user) qs.set('user', user)
    return { type: 'legacy-redirect', targetPath: `/search?${qs.toString()}` }
  }

  if (pathname === '/settings') return { type: 'settings' }

  const folderEntity = pathname.match(/^\/folder\/([^/]+)$/)
  if (folderEntity) {
    const folderId = decodeURIComponent(folderEntity[1])
    if (isUuid(folderId)) return { type: 'folder-entity', folderId }
  }

  if (pathname.startsWith('/doc/')) {
    const rest = pathname.slice('/doc/'.length)
    const decoded = rest.split('/').map((s) => decodeURIComponent(s)).join('/')
    return { type: 'document', pathOrId: decoded, isLegacyDocRoute: true }
  }

  const segments = pathname.split('/').filter(Boolean).map((s) => decodeURIComponent(s))
  if (segments.length >= 2) {
    const [first, second, ...rest] = segments
    if (
      second === 'people' &&
      !GLOBAL_RESERVED_SEGMENTS.has(first) &&
      (workspaces.length === 0 || workspaces.some((w) => w.slug === first))
    ) {
      const workspaceSlug = first
      const workspaceId = workspaceIdForSlug(workspaces, first)
      if (segments.length === 2) {
        return {
          type: 'people-index',
          workspaceSlug,
          ...(workspaceId ? { workspaceId } : {}),
        }
      }
      if (segments.length === 3) {
        return {
          type: 'person',
          userId: rest[0],
          workspaceSlug,
          ...(workspaceId ? { workspaceId } : {}),
        }
      }
    }
    // Shorthand: /{workspaceSlug}/todos -> Basecamp section (not a content document path)
    if (
      segments.length === 2 &&
      BASECAMP_SECTION_PATH_SEGMENTS.has(second) &&
      !GLOBAL_RESERVED_SEGMENTS.has(first) &&
      (workspaces.length === 0 || workspaces.some((w) => w.slug === first))
    ) {
      const strippedPath =
        second === 'triage' ? '/basecamp/triage' : `/basecamp/${second}`
      return {
        type: 'extension-basecamp',
        workspaceSlug: first,
        strippedPath,
        workspaceId: workspaceIdForSlug(workspaces, first),
      }
    }
    if (WORKSPACE_EXTENSION_SEGMENTS.has(second)) {
      if (second === 'basecamp') {
        const restPath = rest.length ? `/${rest.join('/')}` : ''
        const strippedPath = `/basecamp${restPath}`
        return {
          type: 'extension-basecamp',
          workspaceSlug: first,
          strippedPath,
          workspaceId: workspaceIdForSlug(workspaces, first),
        }
      }
      return {
        type: 'extension',
        workspaceSlug: first,
        extensionId: second,
        restPath: rest.join('/'),
      }
    }
  }

  if (segments.length === 1 && !GLOBAL_RESERVED_SEGMENTS.has(segments[0])) {
    const slug = segments[0]
    if (workspaces.some((w) => w.slug === slug)) {
      return { type: 'workspace-home', workspaceSlug: slug }
    }
  }

  if (pathname.startsWith('/basecamp')) {
    const { workspaceId, strippedPath } = stripOptionalBasecampWorkspaceSegment(pathname)
    return {
      type: 'extension-basecamp',
      workspaceSlug: '',
      strippedPath,
      workspaceId,
    }
  }

  if (pathname === '/voice-memos') {
    return { type: 'extension', workspaceSlug: '', extensionId: 'voice-memos', restPath: '' }
  }

  if (pathname === '/product-board') {
    return { type: 'extension', workspaceSlug: '', extensionId: 'product-board', restPath: '' }
  }

  const drivePreview = pathname.match(/^\/drive\/preview\/([^/]+)$/)
  if (drivePreview) {
    return {
      type: 'extension',
      workspaceSlug: '',
      extensionId: 'google-drive-preview',
      restPath: decodeURIComponent(drivePreview[1]),
    }
  }

  const contentPath = pathnameToContentPath(pathname)
  if (contentPath) {
    const storagePath =
      workspaces.length > 0 ? urlPathToStoragePath(contentPath, workspaces) : contentPath
    return { type: 'document', pathOrId: storagePath }
  }

  return { type: 'unknown' }
}

/** Match legacy /basecamp/:uuid/... where first segment is org id not a literal. */
export function parseLegacyBasecampWorkspaceSegment(pathname: string): {
  workspaceId?: string
  strippedPath: string
} {
  return stripOptionalBasecampWorkspaceSegment(pathname)
}

export { BASECAMP_SEGMENT_LITERALS }
